import {
  IonBackButton,
  IonBadge,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonImg,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonPage,
  IonRow,
  IonSearchbar,
  IonSkeletonText,
  IonText,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonToast,
  IonSpinner,
  useIonViewWillLeave,
  IonChip,
  useIonLoading
} from '@ionic/react';
import { alertCircleOutline, cloudOfflineOutline, refresh, scanOutline, syncOutline, addCircleOutline, closeOutline, checkmarkCircleOutline, removeCircleOutline, addOutline, removeOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router';
import EnvironmentBadge from '../../components/EnvironmentBadge';
import { getApi } from '../../api';
import {
  fetchStocktakingDetail,
  fetchStocktakingRows,
  listSessionItems,
  startStocktakingSession,
  bulkAddSessionItems,
  updateSessionItem,
  finalizeSession,
  updateStocktaking,
  fetchStockPoints,
} from '../../api/stocktaking';
import { StocktakingDocument, StocktakingSession, StocktakingSessionItem, StockLocation } from '../../types/stocktaking';
import { useStocktakingRealtime, StocktakingItemState } from '../../hooks/useStocktakingRealtime';
import { useBarcodeQueue } from '../../hooks/useBarcodeQueue';
import { useScannerBridge } from '../../hooks/useScannerBridge';

interface ArticlePicture {
  id: number;
  prio: number;
  path: string;
  folder: string | null;
}

interface ArticleDetail {
  id: number;
  name: string;
  short_desc: string | null;
  long_desc: string;
  price: string;
  category: string;
  sale_price: string | null;
  accounting_connector: string;
  tax: string;
  unit: string;
  manufacturer: string;
  pictures: ArticlePicture[];
  PDF: any[];
  active: boolean;
  webshop_article: boolean;
  quantity: number;
  click: number;
  sold: number;
  featured: boolean;
  breadcrumbs: string[];
  arriving_sum: number;
  arriving_date: string;
}

interface ArticleResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ArticleDetail[];
}

const DEFAULT_QUANTITY = 1;

// Helper function to get current user name
function getCurrentUserName(): string {
  try {
    const meRaw = window.localStorage.getItem('me');
    if (!meRaw) return 'Okänd användare';
    const me = JSON.parse(meRaw);
    return me?.name || me?.username || me?.email || 'Okänd användare';
  } catch {
    return 'Okänd användare';
  }
}

const StocktakingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [doc, setDoc] = useState<StocktakingDocument | null>(null);
  const [session, setSession] = useState<StocktakingSession | null>(null);
  const [initialItems, setInitialItems] = useState<StocktakingSessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState<number>(DEFAULT_QUANTITY);
  const [manualId, setManualId] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast] = useIonToast();
  const [presentAlert] = useIonAlert();
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [scannerActive, setScannerActive] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<ArticleDetail | null>(null);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [modalQuantity, setModalQuantity] = useState<number>(DEFAULT_QUANTITY);
  const [modalItemId, setModalItemId] = useState<string | null>(null);
  const [modalCurrentCount, setModalCurrentCount] = useState<number>(0);
  const [modalTotalQuantityInStock, setModalTotalQuantityInStock] = useState<number | null>(null);
  const [modalStockLocationCode, setModalStockLocationCode] = useState<string | null>(null);
  const [availableStockLocations, setAvailableStockLocations] = useState<StockLocation[]>([]);
  const [stockLocationSearch, setStockLocationSearch] = useState<string>('');
  const [showStockLocationDropdown, setShowStockLocationDropdown] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const { queue, push, acknowledge, isDuplicate } = useBarcodeQueue();

  const fetchArticleDetail = useCallback(async (accountingConnector: string): Promise<ArticleDetail | null> => {
    try {
      setLoadingArticle(true);
      const api = getApi();
      const response = await api.get<ArticleResponse>(`/api/bbe/articles/?accounting_connector=${encodeURIComponent(accountingConnector)}`);
      if (response.data.results && response.data.results.length > 0) {
        return response.data.results[0];
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch article detail', error);
      return null;
    } finally {
      setLoadingArticle(false);
    }
  }, []);

  const showArticleInfo = useCallback(async (itemId: string, itemDescription?: string, currentCount?: number) => {
    const article = await fetchArticleDetail(itemId);
    
    // Hämta totalQuantityInStock och stockLocationCode från initialItems
    const item = initialItems.find(i => i.itemId === itemId);
    const totalQuantityInStock = item?.totalQuantityInStock ?? null;
    const stockLocationCode = item?.stockLocationCode ?? null;
    
    if (article) {
      setSelectedArticle(article);
      setModalItemId(itemId);
      setModalCurrentCount(currentCount ?? 0);
      setModalQuantity(currentCount ?? 0);
      setModalTotalQuantityInStock(totalQuantityInStock);
      setModalStockLocationCode(stockLocationCode);
      setStockLocationSearch(stockLocationCode || '');
      setShowStockLocationDropdown(false);
      setArticleModalOpen(true);
    } else {
      toast({ 
        message: `Ingen detaljerad information hittades för ${itemDescription || itemId}`, 
        color: 'warning', 
        duration: 2500, 
        position: 'bottom' 
      });
    }
  }, [fetchArticleDetail, initialItems, toast]);

  // Filter stock locations based on search input
  const filteredStockLocations = useMemo(() => {
    if (!stockLocationSearch) return availableStockLocations;
    const search = stockLocationSearch.toLowerCase();
    return availableStockLocations.filter(location => 
      location.code.toLowerCase().includes(search) ||
      (location.name && location.name.toLowerCase().includes(search))
    );
  }, [stockLocationSearch, availableStockLocations]);

  const restUpdater = useCallback(
    async (itemId: string, quantity: number) => {
      if (!session?.id) return null;
      try {
        // Find current item to preserve stockLocation data
        const currentItem = initialItems.find(i => i.itemId === itemId);
        
        const updated = await updateSessionItem(session.id, itemId, {
          countedQuantity: quantity,
          stockTakenQuantity: quantity,
          countedBy: getCurrentUserName(),
          // Preserve stock location data
          stockLocationCode: currentItem?.stockLocationCode,
          stockLocationId: currentItem?.stockLocationId,
        });
        return updated;
      } catch (err) {
        console.error('[Stocktaking] REST updater failed', err);
        return null;
      }
    },
    [session, initialItems]
  );

  const { state: wsState, items, queueCount, refreshSnapshot, lastClose, lastError, lockItem, unlockItem, isLockedByMe } = useStocktakingRealtime({
    sessionId: session?.id ?? null,
    initialItems,
    restUpdater,
  });
  const [editingLockedItem, setEditingLockedItem] = useState<string | null>(null);

  const handleStockLocationChange = useCallback((newLocationCode: string) => {
    if (!modalItemId) return;

    const selectedLocation = availableStockLocations.find(loc => loc.code === newLocationCode);
    if (!selectedLocation) return;

    // Optimistic local update
    setModalStockLocationCode(newLocationCode);
    setInitialItems(prev => prev.map(item =>
      item.itemId === modalItemId
        ? {
            ...item,
            stockLocationCode: newLocationCode,
            stockLocationId: selectedLocation.fortnox_id
          }
        : item
    ));

    // Also send a zero-delta count event to propagate location via websocket immediately
    // (Server can ignore quantityDelta if 0 but will broadcast item including these extra fields if supported)
    queueCount(modalItemId, 0, items.find(i => i.itemId === modalItemId)?.countedQuantity);

    toast({
      message: `Lagerplats ändrad till ${newLocationCode}`,
      duration: 1600,
      position: 'bottom',
      color: 'success'
    });
  }, [modalItemId, availableStockLocations, queueCount, items, toast]);

  const handleModalSave = useCallback(() => {
    if (!modalItemId) return;
    
    const item = items.find(i => i.itemId === modalItemId);
    if (!item) return;

    const newCount = modalQuantity;
    const delta = newCount - modalCurrentCount;

    if (!Number.isFinite(newCount) || newCount < 0) {
      toast({ message: 'Ogiltigt antal', color: 'danger', duration: 2000, position: 'bottom' });
      return;
    }

    if (Math.abs(delta) < 1e-6) {
      // No change
      setArticleModalOpen(false);
      return;
    }

    queueCount(modalItemId, delta, newCount);
    push(modalItemId, 'manual', delta);
    toast({ 
      message: modalCurrentCount > 0 ? `${delta > 0 ? '+' : ''}${delta} (nu ${newCount})` : `Räknat: ${newCount}`, 
      duration: 1500, 
      position: 'bottom' 
    });
    
    setArticleModalOpen(false);
  }, [modalItemId, modalQuantity, modalCurrentCount, items, queueCount, push, toast]);

  const scanInFlightRef = useRef(false);

  const handleScan = useCallback(
    async (code: string) => {
      if (!code || scanInFlightRef.current) return;
      scanInFlightRef.current = true; // guard against duplicate events from same scan
      const trimmed = code.trim();
      if (!trimmed) {
        scanInFlightRef.current = false;
        return;
      }

      const existing = items.find(i => i.itemId === trimmed);
      
      // Close modal but keep camera running for next scan
      setScannerActive(false);
      setScannerLoading(false);

      if (!existing) {
        toast({ message: 'Artikeln finns inte i denna inventeringslista', color: 'warning', duration: 2500, position: 'bottom' });
        scanInFlightRef.current = false;
        return;
      }

      await showArticleInfo(existing.itemId, existing.itemDescription, Number(existing.countedQuantity ?? 0));

      // Mark scan complete
      scanInFlightRef.current = false;
    },
    [items, toast, showArticleInfo]
  );

  const { startScanner, stopScanner } = useScannerBridge({ autoStart: false, onScan: handleScan });

  const handleActivateScanner = async () => {
    if (scannerLoading || scannerActive) return;
    
    // Show modal immediately for instant feedback
    setScannerError(null);
    setScannerActive(true);
    setScannerLoading(true);
    
    // Start scanner in background
    try {
      await startScanner();
      setScannerLoading(false);
      toast({ message: 'Skanner aktiverad', duration: 1800, position: 'bottom' });
    } catch (error) {
      console.error('Failed to activate scanner', error);
      setScannerActive(false);
      setScannerLoading(false);
      setScannerError('Kunde inte starta skannern.');
      toast({ message: 'Kunde inte starta skannern.', color: 'danger', duration: 2500, position: 'bottom' });
    }
  };

  const handleDeactivateScanner = () => {
    stopScanner();
    setScannerActive(false);
    setScannerLoading(false);
    toast({ message: 'Skanner avaktiverad', duration: 1800, position: 'bottom' });
  };

  // Fetch stock locations on mount
  useEffect(() => {
    const loadStockLocations = async () => {
      try {
        const response = await fetchStockPoints();
        // Flatten all stock locations from all stock points
        const allLocations: StockLocation[] = [];
        response.data.forEach(stockPoint => {
          allLocations.push(...stockPoint.stock_locations);
        });
        setAvailableStockLocations(allLocations);
      } catch (error) {
        console.error('Failed to fetch stock locations', error);
      }
    };
    loadStockLocations();
  }, []);

  useEffect(() => {
    for (const entry of queue) {
      const match = items.find(item => item.itemId === entry.id);
      if (match && !match.pending) {
        acknowledge(entry.id);
      }
    }
  }, [acknowledge, items, queue]);

  const ensureSession = useCallback(
    async (document: StocktakingDocument) => {
      try {
        const response = await startStocktakingSession({ name: document.name || `Inventering ${document.id}`, fortnoxId: document.id });
        setSession(response);
        return response;
      } catch (error: any) {
        console.error('Failed to start session', error);
        const errorMsg = error?.response?.data?.error;
        
        // Check if already finalized
        if (errorMsg && errorMsg.includes('redan finaliserad')) {
          setIsFinalized(true);
          setSessionError('Denna inventering är redan skickad till Fortnox och kan inte redigeras.');
        }
        
        const existing = error?.response?.data?.session || error?.response?.data;
        if (existing?.id) {
          setSession(existing);
          return existing as StocktakingSession;
        }
        
        if (!isFinalized) {
          setSessionError('Kunde inte starta inventeringssession.');
        }
        throw error;
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const document = await fetchStocktakingDetail(id);
        if (cancelled) return;
        setDoc(document);
        // Detect finalized state from document before attempting to start a session
        if (document.state === 'finalized' || document.state === 'finished') {
          setIsFinalized(true);
        }
        try {
          const baseRows = await fetchStocktakingRows(id);
          if (!cancelled && baseRows.length) {
            setInitialItems(baseRows);
          }
        } catch (rowsError) {
          console.error('Failed to load stocktaking rows', rowsError);
        }
        // Only start a live session if not finalized
        if (!cancelled && !isFinalized) {
          const sessionResponse = await ensureSession(document);
          if (cancelled) return;
          try {
            const itemsResponse = await listSessionItems(sessionResponse.id);
            if (!cancelled && itemsResponse.length) {
              setInitialItems(prev => {
                if (!prev || prev.length === 0) return itemsResponse;
                const map = new Map(prev.map(r => [String(r.itemId), r]));
                for (const item of itemsResponse) {
                  const existing = map.get(String(item.itemId));
                  if (existing) {
                    map.set(String(item.itemId), {
                      ...existing,
                      countedQuantity: item.countedQuantity ?? existing.countedQuantity,
                      stockTakenQuantity: item.stockTakenQuantity ?? item.countedQuantity ?? existing.stockTakenQuantity,
                      lockedBy: item.lockedBy ?? existing.lockedBy,
                    });
                  } else {
                    map.set(String(item.itemId), item);
                  }
                }
                return Array.from(map.values());
              });
            }
          } catch (sessionItemsError) {
            console.error('Failed to load session items', sessionItemsError);
          }
        }
      } catch (error) {
        console.error('Failed to init stocktaking detail', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureSession, id, isFinalized]);

  const handleManualAdd = async () => {
    if (!manualId || !session?.id) return;
    const trimmed = manualId.trim();
    if (!trimmed) return;
    const lowered = trimmed.toLowerCase();
    const existsInList = items.some(i => i.itemId.toLowerCase() === lowered || (i as any).barcode?.toLowerCase?.() === lowered);
    if (!existsInList) {
      presentAlert({
        header: 'Okänd artikel',
        message: `Artikel "${trimmed}" finns inte i denna inventeringslista.
Kontrollera nummer eller skanna etiketten.`,
        buttons: ['OK'],
      });
      return;
    }
    setBusy(true);
    try {
      await bulkAddSessionItems(session.id, [{ itemId: trimmed }]);
      push(trimmed, 'manual', quantity);
      queueCount(trimmed, quantity);
      setManualId('');
    } catch (error) {
      console.error('Failed to add item manually', error);
      toast({ message: 'Kunde inte lägga till artikel.', color: 'danger', duration: 2500 });
    } finally {
      setBusy(false);
    }
  };

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aCounted = (a.countedQuantity ?? 0) > 0;
      const bCounted = (b.countedQuantity ?? 0) > 0;
      if (aCounted !== bCounted) {
        // false (0-count) should come first, so if aCounted is true and bCounted false -> a after b
        return aCounted ? 1 : -1;
      }
      // Within same group sort by itemId
      return a.itemId.localeCompare(b.itemId);
    });
  }, [items]);

  const handleAdjustQuantity = useCallback(
    async (item: StocktakingItemState) => {
      // Show article info with counting modal
      await showArticleInfo(item.itemId, item.itemDescription, Number(item.countedQuantity ?? 0));
    },
    [showArticleInfo]
  );

  const handleFinalize = useCallback(async () => {
    if (!session?.id || !doc?.id) {
      toast({ message: 'Kan inte slutföra - session eller dokument saknas', color: 'danger', duration: 2500, position: 'bottom' });
      return;
    }

    if (queue.length > 0) {
      toast({ message: 'Vänta tills alla rader är synkade innan du slutför', color: 'warning', duration: 3000, position: 'bottom' });
      return;
    }

    const uncountedItems = items.filter(item => (item.countedQuantity ?? 0) === 0);
    if (uncountedItems.length > 0) {
      presentAlert({
        header: 'Oräknade artiklar',
        message: `Det finns ${uncountedItems.length} artiklar som inte är räknade. Vill du ändå slutföra inventeringen?`,
        buttons: [
          { text: 'Avbryt', role: 'cancel' },
          {
            text: 'Slutför ändå',
            handler: () => {
              performFinalize();
            },
          },
        ],
      });
    } else {
      presentAlert({
        header: 'Slutför inventering',
        message: 'Är du säker på att du vill slutföra inventeringen? Datan kommer att skickas till Fortnox och ni kommer inte längre kunna öppna den i appen.',
        buttons: [
          { text: 'Avbryt', role: 'cancel' },
          {
            text: 'Slutför',
            handler: () => {
              performFinalize();
            },
          },
        ],
      });
    }
  }, [session, doc, queue, items, toast, presentAlert]);

  const performFinalize = async () => {
    if (!session?.id || !doc?.id) return;

    setFinalizing(true);
    try {
      await finalizeSession(session.id, { 
        fortnoxId: String(doc.id)
      });
      // Update local document state & backend if possible so re-entry shows finalized status
      try {
        const updatedDoc = await updateStocktaking(doc.id, { id: doc.id, state: 'finalized' } as any);
        setDoc(updatedDoc);
        setIsFinalized(true);
      } catch (updateErr) {
        console.warn('Failed to update stocktaking state to finalized, relying on backend eventual consistency.', updateErr);
        setIsFinalized(true);
      }
      toast({ 
        message: 'Inventeringen har slutförts och skickats till Fortnox!', 
        color: 'success', 
        duration: 3000, 
        position: 'bottom' 
      });
      // Optional: do NOT auto-navigate so user immediately sees read-only state
      // If navigation desired, uncomment below
      // setTimeout(() => { history.push('/stocktaking'); }, 1000);
    } catch (error: any) {
      console.error('Failed to finalize session', error);
      const errorMsg = error?.response?.data?.error || error?.message || 'Kunde inte slutföra inventeringen';
      toast({ 
        message: `Fel vid slutförande: ${errorMsg}`, 
        color: 'danger', 
        duration: 4000, 
        position: 'bottom' 
      });
    } finally {
      setFinalizing(false);
    }
  };

  // Cleanup unlock on unmount if a row left locked locally
  useEffect(() => {
    return () => {
      if (editingLockedItem) {
        unlockItem(editingLockedItem);
      }
    };
  }, [editingLockedItem, unlockItem]);

  // Lock item when modal opens, unlock when it closes
  useEffect(() => {
    if (articleModalOpen && modalItemId) {
      lockItem(modalItemId);
      setEditingLockedItem(modalItemId);
    } else if (!articleModalOpen && editingLockedItem) {
      unlockItem(editingLockedItem);
      setEditingLockedItem(null);
    }
  }, [articleModalOpen, modalItemId, lockItem, unlockItem, editingLockedItem]);

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {scannerActive ? (
              <IonButton onClick={handleDeactivateScanner} color="danger" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonIcon slot="icon-only" icon={closeOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </IonButton>
            ) : (
              <IonBackButton defaultHref="/stocktaking" />
            )}
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {scannerActive ? 'Skanna streckkod' : (doc?.name || `Inventering ${id}`)}
          </IonTitle>
          <div slot="end" style={{ paddingRight: '8px' }}><EnvironmentBadge /></div>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <div 
          id="data-capture-view" 
          style={{ position: 'fixed', inset: 0, zIndex: 20, pointerEvents: 'none', display: scannerActive ? 'block' : 'none' }} 
        />
        
        {/* Loading overlay när skannern startar */}
        {scannerActive && scannerLoading && (
          <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 21,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            pointerEvents: 'none'
          }}>
            <div style={{ textAlign: 'center', color: 'white' }}>
              <IonSpinner name="crescent" style={{ width: '48px', height: '48px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              <div style={{ marginTop: '16px', fontSize: '16px' }}>Startar kamera...</div>
            </div>
          </div>
        )}

        {isFinalized && (
          <IonCard color="success" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardTitle style={{ color: 'white' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Inventering slutförd</IonCardTitle>
            </IonCardHeader>
            <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonNote style={{ color: 'white' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                Denna inventering är redan skickad till Fortnox och kan inte längre redigeras.
              </IonNote>
            </IonCardContent>
          </IonCard>
        )}

        {sessionError && !isFinalized && (
          <IonCard color="warning" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Problem med session</IonCardTitle>
            </IonCardHeader>
            <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonNote color="danger" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{sessionError}</IonNote>
            </IonCardContent>
          </IonCard>
        )}

        {doc && (
          <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{doc.name}</IonCardTitle>
              <IonCardSubtitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{doc.date}</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonGrid placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Status: {doc.state}</IonLabel>
                </IonRow>
                <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Session: {session?.id ?? 'Skapas...'}</IonLabel>
                </IonRow>
              </IonGrid>
              {/* Finalize Button */}
              {!isFinalized && (
              <>
              <IonButton 
                expand="block" 
                color="success" 
                onClick={handleFinalize}
                disabled={finalizing || loading || !session?.id || !doc?.id}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                <IonIcon icon={checkmarkCircleOutline} slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                {finalizing ? 'Slutför inventering...' : 'Slutför inventering'}
              </IonButton>
              {queue.length > 0 && (
                <IonNote color="warning" style={{ display: 'block', marginTop: '8px', textAlign: 'center' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  Vänta tills alla rader är synkade ({queue.length} kvar)
                </IonNote>
              )}
              </>
              )}
            </IonCardContent>
          </IonCard>
        )}

        <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          
          <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {!scannerActive && (
              <IonButton
                expand="block"
                color="primary"
                onClick={handleActivateScanner}
                disabled={scannerLoading || isFinalized}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                <IonIcon icon={scanOutline} slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                {scannerLoading ? 'Startar skanner...' : 'Skanna streckkod'}
              </IonButton>
            )}
            {scannerActive && (
              <IonNote color="success" style={{ display: 'block', textAlign: 'center', fontSize: '1rem', padding: '16px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonIcon icon={scanOutline} style={{ fontSize: '2rem', display: 'block', margin: '0 auto 8px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                Skannern är aktiv. Rikta kameran mot en streckkod.
              </IonNote>
            )}
            {scannerError && (
              <IonNote color="danger" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                {scannerError}
              </IonNote>
            )}
            {queue.length > 0 && (
              <IonNote color="warning" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                {queue.length} rader väntar på synk
              </IonNote>
            )}
          </IonCardContent>
        </IonCard>

        <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Rader</IonCardTitle>
            <IonCardSubtitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              {lastError && (
                <IonBadge color="danger" style={{ marginLeft: 8 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  Fel
                </IonBadge>
              )}
            </IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {lastError && (
              <IonNote color="danger" style={{ display: 'block', marginBottom: 8 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                WebSocket-fel: {lastError}
              </IonNote>
            )}
            {wsState === 'closed' && lastClose?.code && (
              <IonNote color="medium" style={{ display: 'block', marginBottom: 8 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                Anslutningen stängd (kod {lastClose.code}{lastClose.reason ? `: ${lastClose.reason}` : ''}). Försöker återskapa...
              </IonNote>
            )}
            {loading ? (
              <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                {Array.from({ length: 5 }).map((_, index) => (
                  <IonItem key={index} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <h3><IonSkeletonText animated style={{ width: '50%' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /></h3>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
            ) : (
              <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                {sortedItems.map(item => (
                  <IonItem
                    key={item.itemId}
                    button={!isFinalized}
                    detail={!isFinalized}
                    onClick={() => !isFinalized && handleAdjustQuantity(item)}
                    disabled={isFinalized}
                    style={isFinalized ? { opacity: 0.6 } : undefined}
                    placeholder={undefined}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  >
                    <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap', minWidth: 0 }}>
                        {(item.countedQuantity ?? 0) > 0 && (
                          <IonBadge color="success" style={{ flexShrink: 0 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            Räknad
                          </IonBadge>
                        )}
                        {item.lockedBy && !isLockedByMe(item.itemId) && (
                          <IonBadge color="medium" style={{ flexShrink: 0 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            Låst
                          </IonBadge>
                        )}
                        <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.itemDescription || item.itemId}</strong>
                      </div>
                      <p style={{ marginTop: 4 }}>
                        Räknat: {item.countedQuantity ?? 0}
                        {item.countedBy && (
                          <IonBadge color="primary" style={{ marginLeft: 8 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            {item.countedBy}
                          </IonBadge>
                        )}
                      </p>
                    </IonLabel>
                    {item.pending && (
                      <IonBadge color="warning" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonIcon icon={syncOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                      </IonBadge>
                    )}
                    {item.conflict && (
                      <IonBadge color="danger" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonIcon icon={alertCircleOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                      </IonBadge>
                    )}
                    {wsState !== 'open' && (
                      <IonBadge color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonIcon icon={cloudOfflineOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                      </IonBadge>
                    )}
                  </IonItem>
                ))}
              </IonList>
            )}
          </IonCardContent>
        </IonCard>
      </IonContent>

      {/* Article Detail Modal */}
      <IonModal isOpen={articleModalOpen} onDidDismiss={() => setArticleModalOpen(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Artikelinformation</IonTitle>
            <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonButton onClick={() => setArticleModalOpen(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonIcon icon={closeOutline} slot="icon-only" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding" scrollY={true} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          {loadingArticle ? (
            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonSkeletonText animated style={{ width: '100%', height: '200px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                <IonSkeletonText animated style={{ width: '60%' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                <IonSkeletonText animated style={{ width: '80%' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </IonCardContent>
            </IonCard>
          ) : selectedArticle ? (
            <>
              <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                {selectedArticle.pictures && selectedArticle.pictures.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '16px', backgroundColor: '#f5f5f5' }}>
                    <IonImg 
                      src={selectedArticle.pictures[0].path} 
                      alt={selectedArticle.name}
                      style={{ width: 'auto', maxWidth: '100%', height: '200px', objectFit: 'contain' }}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  </div>
                )}
                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{selectedArticle.name}</IonCardTitle>
                  <IonCardSubtitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    Artikelnummer: {selectedArticle.accounting_connector}
                  </IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  {selectedArticle.long_desc && (
                    <IonText placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <p><strong>Beskrivning:</strong> {selectedArticle.long_desc}</p>
                    </IonText>
                  )}
                  <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    {selectedArticle.manufacturer && (
                      <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                          <strong>Tillverkare:</strong> {selectedArticle.manufacturer}
                        </IonLabel>
                      </IonItem>
                    )}
                    <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <strong>Lagersaldo (Fortnox):</strong> {modalTotalQuantityInStock !== null ? modalTotalQuantityInStock : 'Okänt'}
                      </IonLabel>
                    </IonItem>
                  </IonList>
                  
                  {/* Stock location typeahead */}
                  <div style={{ padding: '0 16px', marginTop: '16px' }}>
                    <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <strong>Lagerplats:</strong>
                    </IonLabel>
                    <IonSearchbar
                      onIonChange={(e: any) => {
                        setStockLocationSearch(e.detail.value || '');
                        setShowStockLocationDropdown(true);
                      }}
                      debounce={300}
                      onIonFocus={() => setShowStockLocationDropdown(true)}
                      placeholder="Sök lagerplats..."
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                    {showStockLocationDropdown && filteredStockLocations.length > 0 && (
                      <IonList style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid var(--ion-color-medium)', borderRadius: '4px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        {filteredStockLocations.map((location) => (
                          <IonItem
                            key={location.id}
                            button
                            onClick={() => {
                              handleStockLocationChange(location.code);
                              setStockLocationSearch(location.code);
                              setShowStockLocationDropdown(false);
                            }}
                            placeholder={undefined}
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                          >
                            <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                              {location.code} {location.name && `- ${location.name}`}
                            </IonLabel>
                          </IonItem>
                        ))}
                      </IonList>
                    )}
                  </div>
                </IonCardContent>
              </IonCard>

              {/* Counting Card */}
              <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Räkna artikel</IonCardTitle>
                  {modalCurrentCount > 0 && (
                    <IonCardSubtitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      Tidigare räknat: {modalCurrentCount}
                    </IonCardSubtitle>
                  )}
                </IonCardHeader>
                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      Nytt totalt antal räknat
                    </IonLabel>
                  </IonItem>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                    <IonButton
                      fill="outline"
                      onClick={() => setModalQuantity(Math.max(0, modalQuantity - 1))}
                      disabled={modalQuantity <= 0}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      <IonIcon icon={removeOutline} slot="icon-only" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    </IonButton>
                    <IonInput
                      value={String(modalQuantity)}
                      type="number"
                      inputmode="decimal"
                      step="0.01"
                      min="0"
                      style={{ flex: 1, textAlign: 'center' }}
                      placeholder={undefined}
                      onIonFocus={(e) => {
                        // Scroll the input into view when keyboard appears on iOS
                        setTimeout(() => {
                          const element = e.target as unknown as HTMLElement;
                          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      onIonChange={ev => {
                        const raw = ev.detail.value || '';
                        const num = parseFloat(raw.replace(',', '.'));
                        if (Number.isFinite(num)) {
                          setModalQuantity(num);
                        } else if (raw === '') {
                          setModalQuantity(0);
                        }
                      }}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                    <IonButton
                      fill="outline"
                      onClick={() => setModalQuantity(modalQuantity + 1)}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      <IonIcon icon={addOutline} slot="icon-only" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    </IonButton>
                  </div>
                  <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                    <IonButton 
                      expand="block" 
                      onClick={() => setArticleModalOpen(false)}
                      fill="outline"
                      style={{ flex: 1 }}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      Avbryt
                    </IonButton>
                    <IonButton 
                      expand="block" 
                      onClick={handleModalSave}
                      style={{ flex: 1 }}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      Spara
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>
              {/* Extra padding to prevent keyboard from covering content on iOS */}
              <div style={{ height: '300px' }} />
            </>
          ) : (
            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonNote placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Ingen artikel vald</IonNote>
              </IonCardContent>
            </IonCard>
          )}
        </IonContent>
      </IonModal>
    </IonPage>
  );
};

export default StocktakingDetail;
